import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import type { ApolloError } from '@apollo/client'
import { KibTopLevelAlert } from '@chewy/kib-messaging-react/legacy'
import { Button, Grid, Skeleton } from '@mui/material'
import type { Nil } from '@pbt/pbt-ui-components'
import { Edit as EditIcon } from '@pbt/pbt-ui-components/src/icons'

import type { PreviousMedicalRecord } from '~/api/graphql/generated/types'
import Typography from '~/components/elements/Typography/Typography'
import { ConsentStatusName } from '~/constants/previousMedicalRecord'
import { getSimplePmrMessage, useConsentTexts } from '~/utils/pmrUtils'

import { ClinicBrief } from './ClinicBrief'

import '@chewy/kib-messaging-styles/dist/styles/kib-alert-icon.css'
import '@chewy/kib-messaging-styles/dist/styles/kib-top-level-alert.css'
import styles from './PreviousMedicalRecords.module.scss'

interface PreviousMedicalRecordsProps {
  error?: ApolloError
  loading?: boolean
  onEdit?: () => void
  previousMedicalRecord?: PreviousMedicalRecord | Nil
}

const PreviousMedicalRecords: FC<PreviousMedicalRecordsProps> = ({
  error,
  loading = false,
  onEdit,
  previousMedicalRecord,
}) => {
  const { t } = useTranslation(['Common', 'Errors', 'TimeTable'])
  const { consentText, descriptionText, showConsentLine } = useConsentTexts(
    previousMedicalRecord?.consentStatus?.id,
    t('TimeTable:PMR_CONSENT.CONSENT_NULL_DESCRIPTION'),
  )

  const titleSection = (
    <Grid container item alignItems="center" wrap="nowrap">
      <Typography.H3 className={styles.titleText}>
        {t('Common:PMR_CONSENT.PREVIOUS_MEDICAL_RECORDS_TITLE')}
      </Typography.H3>
      {onEdit && (
        <Button
          className={styles.editButton}
          disabled={loading}
          onClick={onEdit}
        >
          <EditIcon className={styles.editIcon} color="primary" />
          <Typography.H3 className={styles.editText}>
            {t('Common:EDIT_ACTION')}
          </Typography.H3>
        </Button>
      )}
    </Grid>
  )

  if (loading) {
    return (
      <Grid container direction="column">
        {titleSection}
        <Grid
          container
          className={styles.container}
          justifyContent="flex-start"
        >
          <Skeleton height={22} variant="text" width={180} />
        </Grid>
      </Grid>
    )
  }

  if (error) {
    return (
      <Grid container direction="column">
        {titleSection}
        <KibTopLevelAlert
          heading={t('Errors:UNABLE_TO_LOAD_DATA')}
          severity="error"
        />
      </Grid>
    )
  }

  const priorVetCareStatus = previousMedicalRecord?.priorVetCareStatus?.name
  const contacts = previousMedicalRecord?.contacts ?? []

  if (previousMedicalRecord === null) {
    return null
  }

  const simpleMessage = getSimplePmrMessage(priorVetCareStatus)
  if (simpleMessage) {
    return (
      <Grid container direction="column">
        {titleSection}
        <Grid item className={styles.container}>
          <Typography.Paragraph>{simpleMessage}</Typography.Paragraph>
        </Grid>
      </Grid>
    )
  }

  // Yes - Consent plus Previous clinic OR No - Did not consent to PMR
  // Both require priorVetCareStatus === YES and show clinics
  const consentEntity = previousMedicalRecord?.consentStatus
  const showClinics = showConsentLine && contacts.length > 0

  return (
    <Grid container direction="column">
      {titleSection}
      <Grid container className={styles.container} gap={1}>
        <Grid item>
          {showConsentLine && (
            <Typography.H3
              className={
                consentEntity?.name === ConsentStatusName.DENIED
                  ? styles.consentTextError
                  : styles.consentText
              }
            >
              {consentText}
            </Typography.H3>
          )}
          <Typography.Paragraph>{descriptionText}</Typography.Paragraph>
        </Grid>
        {showClinics && (
          <Grid container direction="column" spacing={1}>
            {contacts.map((contact) => (
              <Grid item key={contact.id}>
                <ClinicBrief
                  foldable
                  contact={contact}
                  defaultExpanded={false}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Grid>
    </Grid>
  )
}

export default PreviousMedicalRecords
